import { useEffect, useRef, useState, useCallback } from "react";
import { BACKEND } from "./api";

// Subscribes to a WebSocket room (with ordered replay since last seq) and polls every `pollMs` as a fallback.
export function useLive(room, load, { token, pollMs = 3000, onEvent } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const seq = useRef(0);
  const loadRef = useRef(load);
  const evRef = useRef(onEvent);
  loadRef.current = load;
  evRef.current = onEvent;

  const refresh = useCallback(async () => {
    try {
      setData(await loadRef.current());
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => {
    if (!room) return;
    seq.current = 0;
    refresh();
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [room, pollMs, refresh]);

  useEffect(() => {
    if (!room) return;
    let ws;
    let closed = false;
    let retry;
    let ping;
    let debounce;
    const connect = () => {
      if(closed)return;
      const url = `${BACKEND.replace(/^http/, "ws")}/api/ws?room=${encodeURIComponent(room)}&since=${seq.current}${token ? `&token=${token}` : ""}`;
      ws = new WebSocket(url);
      ws.onopen = () => {
        if(closed){ws.close();return;}
        setConnected(true);
        ping = setInterval(() => ws.readyState === 1 && ws.send("ping"), 20000);
      };
      ws.onmessage = (m) => {
        if(closed)return;
        if (m.data === "pong") return;
        try {
          const msg = JSON.parse(m.data);
          if (msg.seq > seq.current) seq.current = msg.seq;
          if (msg.hello) return;
          if (!msg.replay) evRef.current?.(msg);
          clearTimeout(debounce);
          debounce = setTimeout(refresh, 120);
        } catch (e) {}
      };
      ws.onclose = () => {
        if(closed)return;
        setConnected(false);
        clearInterval(ping);
        if (!closed) retry = setTimeout(connect, 2500);
      };
    };
    // StrictMode mounts and immediately cleans up effects in development.
    // Wait one task so that probe mount never opens an abandoned connection.
    retry=setTimeout(connect,0);
    return () => {
      closed = true;
      clearTimeout(retry);
      clearTimeout(debounce);
      clearInterval(ping);
      if(ws){
        ws.onmessage=null;ws.onclose=null;
        if(ws.readyState===0)ws.onopen=()=>ws.close();
        else if(ws.readyState===1)ws.close();
      }
    };
  }, [room, token, refresh]);

  return { data, error, connected, refresh };
}
